import { getPublicS3Url } from "../../../../../utils/s3PublicUrl";

export const getCouncilTypeFromName = (name = "") => {
  const val = String(name || "").toUpperCase();
  if (val.includes("NATIONAL EXECUTIVE COUNCIL") || /\bNEC\b/.test(val)) return "NEC";
  if (val.includes("STATE EXECUTIVE COUNCIL") || /\bSEC\b/.test(val)) return "SEC";
  if (val.includes("REGIONAL EXECUTIVE COUNCIL") || /\bREC\b/.test(val)) return "REC";
  if (val.includes("DISTRICT EXECUTIVE COUNCIL") || /\bDEC\b/.test(val)) return "DEC";
  return "";
};

export const getEligibleCertificateOptions = (committeeRows = [], councils = []) => {
  if (!Array.isArray(committeeRows) || committeeRows.length === 0) return [];

  const options = [];
  committeeRows.forEach((row, rowIndex) => {
    const council = councils.find((c) => String(c?._id || "") === String(row?.council || ""));
    const councilName = council?.councilName || council?.name || "";
    const councilType = getCouncilTypeFromName(councilName);
    if (!["NEC", "SEC", "REC", "DEC"].includes(councilType)) return;

    const designationIds = Array.isArray(row?.designations) ? row.designations : [];
    designationIds.forEach((designationId) => {
      const designationOption = (row?.designationOptions || []).find(
        (d) => String(d?._id || "") === String(designationId || ""),
      );
      const designationTitle =
        designationOption?.title ||
        designationOption?.label ||
        String(designationId || "");
      const committeeObj =
        (row?.committees || []).find((c) => String(c?._id || "") === String(row?.committee || "")) ||
        (row?.allCommittees || []).find((c) => String(c?._id || "") === String(row?.committee || ""));
      const committeeName = committeeObj?.name || committeeObj?.committeeName || "Committee";

      options.push({
        key: `${rowIndex}::${designationId}`,
        rowIndex,
        committeeId: row?.committee || "",
        designationId,
        designationTitle,
        councilType,
        councilName,
        committeeName,
        committeeStateId: row?.committeeState || "",
      });
    });
  });

  return options;
};

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Image load failed: ${src}`));
    img.src = src;
  });

const extractPersonName = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "object") {
    const joined = [value.firstName, value.middleName, value.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    return joined || String(value.name || value.fullName || "").trim();
  }
  return "";
};

const extractStatePresidentName = (payload = {}) =>
  extractPersonName(payload?.statePresidentName) ||
  "";

const extractStatePresidentSignature = (payload = {}) =>
  payload?.statePresidentSignature ||
  "";

const getSignatureCandidates = (rawValue = "") => {
  const raw = String(rawValue || "").trim();
  if (!raw) return [];

  const candidates = [];
  const addCandidate = (value) => {
    const normalized = String(value || "").trim();
    if (!normalized) return;
    if (!candidates.includes(normalized)) candidates.push(normalized);
  };

  addCandidate(raw);
  addCandidate(getPublicS3Url(raw));

  if (!/^https?:\/\//i.test(raw)) {
    const withoutLeadingSlash = raw.replace(/^\/+/, "");
    const withoutPublicPrefix = withoutLeadingSlash.replace(/^public\//i, "");
    addCandidate(getPublicS3Url(withoutPublicPrefix));
  }

  return candidates;
};

const loadImageWithCandidates = async (rawSrc = "") => {
  const candidates = getSignatureCandidates(rawSrc);
  for (const src of candidates) {
    try {
      // Encode spaces and special chars but keep URL structure intact.
      const encodedSrc = encodeURI(src);
      // eslint-disable-next-line no-await-in-loop
      const img = await loadImage(encodedSrc);
      if (img) return img;
    } catch (_error) {
      // Try next candidate.
    }
  }
  return null;
};

export const resolveStatePresidentDetails = async ({
  userForVefication,
  preferredStateId = "",
  getFallbackStateId,
  axios,
  baseUrl,
}) => {
  const directState = userForVefication?.State;
  const directName = extractStatePresidentName({
    statePresidentName: directState?.statePresidentName,
  });

  const directSignature = extractStatePresidentSignature({
    statePresidentSignature: directState?.statePresidentSignature,
  }) ||
    extractStatePresidentSignature({
      statePresidentSignature: userForVefication?.statePresidentSignature,
    });

  if (directName || directSignature) {
    return {
      name: directName || "",
      signatureUrl: getPublicS3Url(directSignature || ""),
    };
  }

  const stateId =
    preferredStateId ||
    directState?._id ||
    (typeof getFallbackStateId === "function" ? getFallbackStateId() : "");
  if (!stateId || !baseUrl) return { name: "", signatureUrl: "" };

  try {
    const stateRes = await axios.get(`${baseUrl}/state/${stateId}`);
    const payload = stateRes?.data?.data ?? stateRes?.data ?? {};
    return {
      name: extractStatePresidentName(payload),
      signatureUrl: getPublicS3Url(extractStatePresidentSignature(payload)),
    };
  } catch (_error) {
    return { name: "", signatureUrl: "" };
  }
};

const wrapText = (ctx, text, x, y, maxWidthPx, lh) => {
  const words = String(text || "").split(" ");
  const lines = [];
  let line = "";
  words.forEach((word) => {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidthPx && line) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  });
  if (line) lines.push(line);
  lines.forEach((ln, idx) => ctx.fillText(ln, x, y + idx * lh));
};

export const buildCertificateCanvas = async ({
  selectedOption,
  userForVefication,
  templateSrc,
  founderSignSrc,
  nationalPresidentSignSrc,
  founderName,
  nationalPresidentName,
  tenureText,
  statePresidentDetails,
}) => {
  const fullName = [userForVefication?.userFirstName, userForVefication?.userMiddleName, userForVefication?.userLastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  const stateName = userForVefication?.State?.stateName || userForVefication?.State?.name || "";

  const templateImg = await loadImage(templateSrc);
  if (!templateImg) throw new Error("Certificate template not found.");

  const [stateSignImg, founderSignImg, nationalPresidentSignImg] = await Promise.all([
    statePresidentDetails?.signatureUrl
      ? loadImageWithCandidates(statePresidentDetails.signatureUrl)
      : Promise.resolve(null),
    loadImage(founderSignSrc).catch(() => null),
    loadImage(nationalPresidentSignSrc).catch(() => null),
  ]);

  const canvas = document.createElement("canvas");
  canvas.width = templateImg.width;
  canvas.height = templateImg.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Failed to create certificate canvas context.");

  ctx.drawImage(templateImg, 0, 0, canvas.width, canvas.height);

  const centerX = Math.floor(canvas.width / 2);
  const maxTextWidth = Math.floor(canvas.width * 0.70);
  const nameY = Math.floor(canvas.height * 0.43);
  const sentenceStartY = Math.floor(canvas.height * 0.466);
  const lineHeight = Math.floor(canvas.height * 0.016);

  ctx.fillStyle = "#222";
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";

  ctx.font = "italic 700 58px Georgia";
  ctx.fillText(fullName || "Member Name", centerX, nameY);

  const pronoun = (userForVefication?.gender || "").toLowerCase() === "female" ? "her" : "him";
  const possessivePronoun = (userForVefication?.gender || "").toLowerCase() === "female" ? "her" : "his";
  const description = `as ${selectedOption.designationTitle}${stateName ? `, ${stateName}` : ""} for the term of office 2025 & 2026. We wish ${pronoun} great success during ${possessivePronoun} tenure.`;
  ctx.font = "500 30px Poppins";
  wrapText(ctx, description, centerX, sentenceStartY, maxTextWidth, lineHeight);

  const footerYHigh = Math.floor(canvas.height * 0.79);
  const footerYLow = Math.floor(canvas.height * 0.865);
  const leftX = Math.floor(canvas.width * 0.30);
  const centerXFooter = Math.floor(canvas.width * 0.50);
  const rightX = Math.floor(canvas.width * 0.70);
  const signW = Math.floor(canvas.width * 0.14);
  const signH = Math.floor(signW * 0.42);

  const drawFooterBlock = (x, y, signImg, name, roleLine1, roleLine2) => {
    if (signImg) {
      ctx.drawImage(signImg, x - Math.floor(signW / 2), y - signH, signW, signH);
    }
    const nameOffsetY = 30;
    const roleLine1OffsetY = 68;
    const roleLine2OffsetY = 104;
    ctx.font = "700 42px Poppins";
    ctx.fillText(name || "-", x, y + nameOffsetY, Math.floor(canvas.width * 0.24));
    ctx.font = "500 34px Poppins";
    if (roleLine1) ctx.fillText(roleLine1, x, y + roleLine1OffsetY, Math.floor(canvas.width * 0.25));
    if (roleLine2) ctx.fillText(roleLine2, x, y + roleLine2OffsetY, Math.floor(canvas.width * 0.25));
  };

  const resolvedStatePresidentName = String(statePresidentDetails?.name || "").trim();
  const statePresidentLabel =
    resolvedStatePresidentName ||
    `State President${stateName ? `, ${stateName}` : ""}`;
  const stateRole = `State President${stateName ? `, ${stateName}` : ""}`;

  drawFooterBlock(leftX, footerYHigh, stateSignImg, statePresidentLabel, stateRole, tenureText);
  drawFooterBlock(centerXFooter, footerYLow, founderSignImg, founderName, "Founder", "");
  drawFooterBlock(rightX, footerYHigh, nationalPresidentSignImg, nationalPresidentName, "National President", tenureText);

  return { canvas, fullName };
};

export const sanitizeFileSegment = (value, fallback = "value") => {
  const normalized = String(value || "")
    .trim()
    .replace(/[^a-z0-9]+/gi, "_")
    .replace(/^_+|_+$/g, "");
  return normalized || fallback;
};

export const downloadCanvasAsPdf = async (canvas, fileName) => {
  if (!canvas) throw new Error("Certificate canvas is required.");

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.95));
  if (!blob) throw new Error("Failed to prepare certificate image.");
  const bytes = new Uint8Array(await blob.arrayBuffer());

  const width = canvas.width;
  const height = canvas.height;
  const contentStream = `q\n${width} 0 0 ${height} 0 0 cm\n/Im0 Do\nQ`;

  const encoder = new TextEncoder();
  const text = (value) => encoder.encode(value);
  const concatBytes = (parts) => {
    const total = parts.reduce((sum, part) => sum + part.length, 0);
    const output = new Uint8Array(total);
    let offset = 0;
    parts.forEach((part) => {
      output.set(part, offset);
      offset += part.length;
    });
    return output;
  };

  const obj1 = text("1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n");
  const obj2 = text("2 0 obj\n<< /Type /Pages /Count 1 /Kids [3 0 R] >>\nendobj\n");
  const obj3 = text(
    `3 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${width} ${height}] /Resources << /XObject << /Im0 4 0 R >> >> /Contents 5 0 R >>\nendobj\n`,
  );
  const obj4Header = text(
    `4 0 obj\n<< /Type /XObject /Subtype /Image /Width ${width} /Height ${height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${bytes.length} >>\nstream\n`,
  );
  const obj4Footer = text("\nendstream\nendobj\n");
  const obj4 = concatBytes([obj4Header, bytes, obj4Footer]);
  const obj5 = text(
    `5 0 obj\n<< /Length ${contentStream.length} >>\nstream\n${contentStream}\nendstream\nendobj\n`,
  );

  const header = text("%PDF-1.4\n");
  const objects = [obj1, obj2, obj3, obj4, obj5];
  const offsets = [0];
  let cursor = header.length;
  objects.forEach((obj) => {
    offsets.push(cursor);
    cursor += obj.length;
  });

  let xref = `xref\n0 ${objects.length + 1}\n`;
  xref += "0000000000 65535 f \n";
  for (let i = 1; i <= objects.length; i += 1) {
    xref += `${String(offsets[i]).padStart(10, "0")} 00000 n \n`;
  }
  const xrefBytes = text(xref);
  const trailerBytes = text(
    `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${cursor}\n%%EOF`,
  );
  const pdfBytes = concatBytes([header, ...objects, xrefBytes, trailerBytes]);
  const pdfBlob = new Blob([pdfBytes], { type: "application/pdf" });
  const url = URL.createObjectURL(pdfBlob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};
